import { useEffect, useState } from "react";
import { useQuery, useMutation, useApolloClient } from "@apollo/client/react";
import { GET_REQUIREMENTS, GET_STATS, CREATE_REQUIREMENT, GET_SOURCES, GET_USERS } from "@/graphql/queries";
import { mapToSnakeCase } from "@/graphql/mappers";
import { useApp } from "@/context/AppContext";
import { TagSelector } from "@/components/TagSelector";
import { selectableOwnerNames } from "@/users";
import type { Requirement, Source } from "@/types";

export function CreateRequirement() {
  const { createDriverRequirementId, navigateTo, setCurrentView, activeUser } = useApp();
  const client = useApolloClient();

  const [formData, setFormData] = useState({
    id: "",
    title: "",
    requirementDrivers: (createDriverRequirementId ? [createDriverRequirementId] : []) as string[],
    description: "",
    requirementLevel: "implementation",
    status: "",
    discipline: "",
    category: "",
    owner: "",
    tags: [] as string[],
    sourceDriverIds: [] as string[],
  });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [requirementDriverSearch, setRequirementDriverSearch] = useState("");
  const [requirementDriverPickerOpen, setRequirementDriverPickerOpen] = useState(false);
  const [sourceDriverSearch, setSourceDriverSearch] = useState("");
  const [sourceDriverPickerOpen, setSourceDriverPickerOpen] = useState(false);

  const { data: statsRaw } = useQuery<any>(GET_STATS);
  const { data: reqsRaw } = useQuery<any>(GET_REQUIREMENTS);
  const { data: sourcesRaw } = useQuery<any>(GET_SOURCES);
  const { data: usersRaw } = useQuery<any>(GET_USERS);

  useEffect(() => {
    if (!createDriverRequirementId) return;
    setFormData(prev => prev.requirementDrivers.includes(createDriverRequirementId)
      ? prev
      : { ...prev, requirementDrivers: [createDriverRequirementId, ...prev.requirementDrivers] });
  }, [createDriverRequirementId]);

  const stats = statsRaw ? mapToSnakeCase(statsRaw.stats) : undefined;
  const requirements: Requirement[] = reqsRaw ? mapToSnakeCase(reqsRaw.requirements) : [];
  const allSources: Source[] = sourcesRaw ? sourcesRaw.sources : [];
  const ownerOptions: string[] = usersRaw ? selectableOwnerNames(mapToSnakeCase(usersRaw.users).users) : [];
  const availableRequirementDrivers = requirements
    .filter(r => r.id !== formData.id.trim() && !formData.requirementDrivers.includes(r.id))
    .sort((a, b) => a.id.localeCompare(b.id));
  const filteredRequirementDrivers = availableRequirementDrivers.filter(r => requirementDriverOptionLabel(r).toLowerCase().includes(requirementDriverSearch.toLowerCase()));
  const availableSourceDrivers = allSources.filter(s => !formData.sourceDriverIds.includes(s.id));
  const filteredSourceDrivers = availableSourceDrivers.filter(s => `${s.id} ${s.label}`.toLowerCase().includes(sourceDriverSearch.toLowerCase()));

  const [createMutation] = useMutation<any>(CREATE_REQUIREMENT, {
    onCompleted: async (data) => {
      await client.refetchQueries({ include: [GET_REQUIREMENTS, GET_STATS] });
      if (data?.createRequirement?.id) {
        navigateTo("requirements", data.createRequirement.id);
      }
    },
    onError: (err) => {
      setError(err.message);
      setSubmitting(false);
    },
  });

  const canSubmit = formData.id.trim() && formData.title.trim() && !submitting;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    createMutation({
      variables: {
        input: {
          id: formData.id.trim(),
          title: formData.title.trim(),
          description: formData.description.trim() || undefined,
          requirementLevel: formData.requirementLevel || undefined,
          status: formData.status || undefined,
          discipline: formData.discipline || undefined,
          category: formData.category || undefined,
          owner: formData.owner || undefined,
          tags: formData.tags.length > 0 ? formData.tags : undefined,
          sources: formData.sourceDriverIds.length > 0 ? formData.sourceDriverIds : undefined,
          requirementDrivers: formData.requirementDrivers.length > 0 ? formData.requirementDrivers : undefined,
        },
      },
    });
  }

  function handleCancel() {
    setCurrentView("requirements");
  }

  function setField(field: string, value: string) {
    setFormData(prev => ({ ...prev, [field]: value }));
  }

  const driverReq = createDriverRequirementId
    ? requirements.find((r: Requirement) => r.id === createDriverRequirementId)
    : null;

  return (
    <div className="flex flex-col h-full overflow-auto">
      <div className="max-w-2xl mx-auto w-full px-7 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            className="text-xs text-text2 hover:text-accent transition-colors bg-transparent border-none cursor-pointer mb-3 flex items-center gap-1"
            onClick={handleCancel}
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-3 h-3">
              <path d="M10 3L5 8l5 5" />
            </svg>
            Back to Requirements
          </button>
          <h1 className="text-xl font-semibold text-text leading-tight">New Requirement</h1>
          <p className="text-xs text-text2 mt-1">
            {createDriverRequirementId
              ? <>Creating requirement driven by <span className="text-accent font-medium">{createDriverRequirementId}</span></>
              : "Create a new root or driven requirement"
            }
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-5 p-3 bg-red/10 border border-red/30 rounded-default text-red text-xs">
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          {/* ID */}
          <FormField label="Requirement ID" required>
            <input
              type="text"
              value={formData.id}
              onChange={e => setField("id", e.target.value)}
              placeholder="e.g. REQ-FW-001"
              autoFocus
              className="w-full px-3 py-2 bg-bg border border-border rounded-default text-text text-sm outline-none transition-colors focus:border-accent placeholder:text-text2"
            />
          </FormField>

          {/* Title */}
          <FormField label="Title" required>
            <input
              type="text"
              value={formData.title}
              onChange={e => setField("title", e.target.value)}
              placeholder="e.g. Interface - Dial Controller"
              className="w-full px-3 py-2 bg-bg border border-border rounded-default text-text text-sm outline-none transition-colors focus:border-accent placeholder:text-text2"
            />
          </FormField>

          {createDriverRequirementId && driverReq && (
            <div className="rounded-default border border-border bg-bg2 px-3 py-2 text-xs text-text2">
              Initial requirement driver is automatically set to <span className="text-accent font-medium">{createDriverRequirementId}</span>
              <div className="mt-1 text-[11px]">
                <span className="text-text">{driverReq.level}</span> · {driverReq.description || driverReq.title || "No description"}
              </div>
            </div>
          )}

          {/* Description */}
          <FormField label="Description">
            <textarea
              value={formData.description}
              onChange={e => setField("description", e.target.value)}
              placeholder="Describe the requirement..."
              rows={4}
              className="w-full px-3 py-2 bg-bg border border-border rounded-default text-text text-sm outline-none transition-colors resize-y min-h-[80px] focus:border-accent placeholder:text-text2"
            />
          </FormField>

          {/* Level + Status row */}
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Level">
              <select
                value={formData.requirementLevel}
                onChange={e => setField("requirementLevel", e.target.value)}
                className="w-full px-3 py-2 bg-bg border border-border rounded-default text-text text-sm outline-none transition-colors focus:border-accent"
                aria-label="Requirement level"
              >
                {REQUIREMENT_LEVELS.map(level => <option key={level} value={level}>{level}</option>)}
              </select>
            </FormField>

            <FormField label="Status">
              <select
                value={formData.status}
                onChange={e => setField("status", e.target.value)}
                className="w-full px-3 py-2 bg-bg border border-border rounded-default text-text text-sm outline-none transition-colors focus:border-accent"
              >
                <option value="">Unspecified</option>
                {(stats?.statuses || ["Derived", "Partial", "Proposed", "Unspecified"]).map((s: string) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </FormField>
          </div>

          {/* Discipline + Category row */}
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Discipline">
              <select
                value={formData.discipline}
                onChange={e => setField("discipline", e.target.value)}
                className="w-full px-3 py-2 bg-bg border border-border rounded-default text-text text-sm outline-none transition-colors focus:border-accent"
              >
                <option value="">None</option>
                {(stats?.disciplines || []).map((d: string) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </FormField>

            <FormField label="Category">
              <select
                value={formData.category}
                onChange={e => setField("category", e.target.value)}
                className="w-full px-3 py-2 bg-bg border border-border rounded-default text-text text-sm outline-none transition-colors focus:border-accent"
              >
                <option value="">None</option>
                {(stats?.categories || []).map((c: string) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </FormField>
          </div>

          {/* Owner row */}
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Owner">
              <select
                value={formData.owner}
                onChange={e => setField("owner", e.target.value)}
                aria-label="Owner"
                className="w-full px-3 py-2 bg-bg border border-border rounded-default text-text text-sm outline-none transition-colors focus:border-accent"
              >
                <option value="">Unassigned</option>
                {ownerOptions.map((o: string) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </FormField>
          </div>

          {/* Tags */}
          <FormField label="Tags" hint="Select an existing tag or add a new one">
            <TagSelector
              selectedTags={formData.tags}
              availableTags={stats?.tags || []}
              onChange={tags => setFormData(prev => ({ ...prev, tags }))}
            />
          </FormField>

          {/* Source drivers */}
          <FormField label="Source Drivers" hint="Select source documents that drive this requirement">
            <div className="flex min-w-0 flex-col gap-1" data-testid="source-selector">
              {formData.sourceDriverIds.map(sid => {
                const src = allSources.find(s => s.id === sid);
                return (
                  <div key={sid} className="flex min-w-0 items-center gap-1">
                    <span className="min-w-0 flex-1 truncate text-xs text-text" title={src ? src.label : sid}>{src ? src.label : sid}</span>
                    <button type="button" className="shrink-0 text-red text-xs bg-transparent border-none cursor-pointer px-1" onClick={() => setFormData(prev => ({ ...prev, sourceDriverIds: prev.sourceDriverIds.filter(s => s !== sid) }))}>x</button>
                  </div>
                );
              })}
              {availableSourceDrivers.length > 0 && (
                <div
                  className="relative min-w-0"
                  onBlur={event => {
                    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setSourceDriverPickerOpen(false);
                  }}
                >
                  <input
                    className="w-full min-w-0 bg-bg border border-border rounded-default px-2 py-1 text-text text-xs outline-none focus:border-accent placeholder:text-text2"
                    aria-label="Link source"
                    value={sourceDriverSearch}
                    placeholder="Search source driver..."
                    onFocus={() => setSourceDriverPickerOpen(true)}
                    onClick={() => setSourceDriverPickerOpen(true)}
                    onChange={e => {
                      setSourceDriverSearch(e.target.value);
                      setSourceDriverPickerOpen(true);
                    }}
                  />
                  {sourceDriverPickerOpen && (
                    <div className="absolute left-0 right-0 z-30 mt-1 max-h-52 overflow-auto rounded-default border border-border bg-bg2 shadow-lg" data-testid="source-driver-options">
                      {filteredSourceDrivers.length === 0 ? (
                        <div className="px-2 py-1.5 text-xs text-text2">No matching source drivers.</div>
                      ) : filteredSourceDrivers.map(s => (
                        <button
                          key={s.id}
                          type="button"
                          className="block w-full truncate bg-transparent px-2 py-1.5 text-left text-xs text-text hover:bg-bg3"
                          title={s.label}
                          onMouseDown={event => event.preventDefault()}
                          onClick={() => {
                            setFormData(prev => ({ ...prev, sourceDriverIds: [...prev.sourceDriverIds, s.id] }));
                            setSourceDriverSearch("");
                            setSourceDriverPickerOpen(false);
                          }}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </FormField>

          {/* Requirement drivers */}
          <FormField label="Requirement Drivers" hint="Select requirements that drive this requirement">
            <div className="flex min-w-0 flex-col gap-1" data-testid="requirement-driver-selector">
              {formData.requirementDrivers.map(driverId => {
                const driver = requirements.find(r => r.id === driverId);
                const label = `${driverId}${driver?.title ? ` — ${driver.title}` : ""}`;
                return (
                  <div key={driverId} className="flex min-w-0 items-center gap-1">
                    <span className="min-w-0 flex-1 truncate text-xs text-text" title={label}>{label}</span>
                    <button
                      type="button"
                      className="shrink-0 text-red text-xs bg-transparent border-none cursor-pointer px-1"
                      onClick={() => setFormData(prev => ({ ...prev, requirementDrivers: prev.requirementDrivers.filter(id => id !== driverId) }))}
                      aria-label={`Remove requirement driver ${driverId}`}
                    >x</button>
                  </div>
                );
              })}
              {availableRequirementDrivers.length > 0 && (
                <div
                  className="relative min-w-0"
                  onBlur={event => {
                    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setRequirementDriverPickerOpen(false);
                  }}
                >
                  <input
                    className="w-full min-w-0 bg-bg border border-border rounded-default px-2 py-1 text-text text-xs outline-none focus:border-accent placeholder:text-text2"
                    value={requirementDriverSearch}
                    onFocus={() => setRequirementDriverPickerOpen(true)}
                    onClick={() => setRequirementDriverPickerOpen(true)}
                    onChange={e => {
                      setRequirementDriverSearch(e.target.value);
                      setRequirementDriverPickerOpen(true);
                    }}
                    aria-label="Link requirement driver"
                    placeholder="Search requirement driver..."
                  />
                  {requirementDriverPickerOpen && (
                    <div className="absolute left-0 right-0 z-30 mt-1 max-h-52 overflow-auto rounded-default border border-border bg-bg2 shadow-lg" data-testid="requirement-driver-options">
                      {filteredRequirementDrivers.length === 0 ? (
                        <div className="px-2 py-1.5 text-xs text-text2">No matching requirement drivers.</div>
                      ) : filteredRequirementDrivers.map(r => {
                        const label = requirementDriverOptionLabel(r);
                        return (
                          <button
                            key={r.id}
                            type="button"
                            className="block w-full truncate bg-transparent px-2 py-1.5 text-left text-xs text-text hover:bg-bg3"
                            title={label}
                            onMouseDown={event => event.preventDefault()}
                            onClick={() => {
                              setFormData(prev => ({ ...prev, requirementDrivers: [...prev.requirementDrivers, r.id] }));
                              setRequirementDriverSearch("");
                              setRequirementDriverPickerOpen(false);
                            }}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </FormField>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-3 border-t border-border">
            <button
              type="submit"
              disabled={!canSubmit}
              className="px-5 py-2 bg-accent text-white border border-accent rounded-default text-sm font-medium cursor-pointer transition-colors hover:bg-accent2 hover:border-accent2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Creating..." : "Create Requirement"}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="px-5 py-2 bg-transparent text-text2 border border-border rounded-default text-sm cursor-pointer transition-colors hover:text-text hover:border-text2"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const REQUIREMENT_LEVELS = ["system", "subsystem", "subgroup", "implementation"];


function requirementDriverOptionLabel(requirement: Requirement): string {
  return truncateText(`${requirement.id} — ${requirement.title || requirement.description || requirement.level}`, 86);
}


function truncateText(value: string, maxLength: number): string {
  if (value.length <= maxLength) return value;
  return `${value.slice(0, maxLength - 3)}...`;
}

function FormField({ label, required, hint, children }: { label: string; required?: boolean; hint?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline gap-1.5">
        <label className="text-xs text-text font-medium">
          {label}
          {required && <span className="text-red ml-0.5">*</span>}
        </label>
        {hint && <span className="text-[10px] text-text2">{hint}</span>}
      </div>
      {children}
    </div>
  );
}
